import React, { useState } from 'react'
import { Search, User, Phone, FileText, UserPlus, Loader2, AlertCircle } from 'lucide-react'
import { searchMainSystem, pullPatientFromMain, getCenters } from '../../api/insuranceAPI'
import { useAuth } from '../../contexts/AuthContext'

const PatientSearch = () => {
  const { user } = useAuth()
  const hasAllCenterAccess = user?.role === 'super_admin' || user?.role === 'cmo'
  const [searchType, setSearchType] = useState('uhid')
  const [searchValue, setSearchValue] = useState('')
  const [centerCode, setCenterCode] = useState('')
  const [centers, setCenters] = useState([])
  const [loadingCenters, setLoadingCenters] = useState(false)
  const [results, setResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [isPulling, setIsPulling] = useState(null)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  // Fetch centers on mount
  React.useEffect(() => {
    const fetchCenters = async () => {
      setLoadingCenters(true)
      try {
        const response = await getCenters()
        console.log('Centers API Response:', response.data)
        const centersData = response.data.data?.centers || []
        console.log('Centers Data:', centersData)
        setCenters(centersData)
      } catch (err) {
        console.error('Failed to fetch centers:', err)
        console.error('Error response:', err.response?.data)
        setError('Failed to load centers. Please refresh the page.')
      } finally {
        setLoadingCenters(false)
      }
    }
    fetchCenters()
  }, [])

  // Initialize center code from user data
  React.useEffect(() => {
    if (user) {
      const userCenterCode = localStorage.getItem('center_code')
      // Auto-populate only for center-restricted users
      if (userCenterCode && !hasAllCenterAccess) {
        setCenterCode(userCenterCode)
      }
    }
  }, [user, hasAllCenterAccess])

  const handleSearch = async (e) => {
    e.preventDefault()
    
    if (!searchValue.trim()) {
      setError('Please enter a search value')
      return
    }
    
    if (!centerCode.trim()) {
      setError('Please enter a center code')
      return
    }

    setIsSearching(true)
    setError(null)
    setSuccessMessage(null)
    
    try {
      const response = await searchMainSystem({
        search_type: searchType,
        search_value: searchValue,
        center_code: centerCode
      })
      
      setResults(response.data.data.results || [])
      
      if (response.data.data.results.length === 0) {
        setError('No patients found matching your search')
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to search patients')
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const handlePullPatient = async (personalId) => {
    setIsPulling(personalId)
    setError(null)
    setSuccessMessage(null)
    
    try {
      const response = await pullPatientFromMain(personalId)
      setSuccessMessage(`Patient successfully imported to insurance system!`)
      
      // Remove pulled patient from results
      setResults(results.filter(p => p.personal_id !== personalId))
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to pull patient')
    } finally {
      setIsPulling(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Patient Search</h1>
        <p className="text-gray-600 mt-1">Search for patients in the main system and import to portal</p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {hasAllCenterAccess && (
          <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded-lg text-sm">
            <strong>{user?.role === 'cmo' ? 'Insurance Manager' : 'Insurance Admin'}:</strong> You have access to all centers. Select a center to search.
          </div>
        )}
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Center Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Center {hasAllCenterAccess && <span className="text-red-500">*</span>}
              </label>
              {hasAllCenterAccess ? (
                <select
                  value={centerCode}
                  onChange={(e) => setCenterCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loadingCenters}
                >
                  <option value="">Select Center</option>
                  {centers.map((center) => (
                    <option key={center.center_code} value={center.center_code}>
                      {center.location_name} ({center.center_code})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={centerCode}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                  title="Center assigned to your account"
                />
              )}
            </div>

            {/* Search Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search By</label>
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="uhid">UHID</option>
                <option value="mobile">Mobile Number</option>
                <option value="name">Patient Name</option>
              </select>
            </div>

            {/* Search Value */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {searchType === 'uhid' && 'Enter UHID'}
                {searchType === 'mobile' && 'Enter Mobile Number'}
                {searchType === 'name' && 'Enter Patient Name'}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={
                    searchType === 'uhid' ? 'e.g., EP001234' :
                    searchType === 'mobile' ? 'e.g., 9876543210' :
                    'e.g., John Doe'
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Search Button */}
          <button
            type="submit"
            disabled={isSearching}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSearching ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Searching...
              </>
            ) : (
              <>
                <Search size={20} />
                Search Patients
              </>
            )}
          </button>
        </form>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start gap-2">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Search Results ({results.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((patient) => (
              <div key={patient.personal_id || patient.uhid_no} className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-100 p-2 rounded-full">
                      <User className="text-blue-600" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        {patient.title ? `${patient.title} ${patient.full_name}` : patient.full_name}
                      </h3>
                      <p className="text-sm text-gray-500">{patient.uhid_no}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone size={16} />
                    <span>{patient.mobile || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User size={16} />
                    <span>{patient.gender || 'N/A'}</span>
                  </div>
                  {patient.ip_no && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FileText size={16} />
                      <span>IP: {patient.ip_no}</span>
                    </div>
                  )}
                  {patient.center_code && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FileText size={16} />
                      <span>Center: {patient.center_code}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handlePullPatient(patient.personal_id)}
                  disabled={isPulling === patient.personal_id}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isPulling === patient.personal_id ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Importing...
                    </>
                  ) : (
                    <>
                      <UserPlus size={18} />
                      Pull to Insurance
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isSearching && results.length === 0 && !error && (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <Search className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-800 mb-2">No Search Results</h3>
          <p className="text-gray-600">Enter search criteria above to find patients in the main system</p>
        </div>
      )}
    </div>
  )
}

export default PatientSearch
